import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { Providers, useAuth, isStaff } from "./context/AppContext";
import Layout from "./components/Layout";
import { Loading, Section, Empty } from "./components/Common";
import Home from "./pages/Home";
import { Courses, CourseDetail } from "./pages/Courses";
import CalendarPage from "./pages/CalendarPage";
import Apply from "./pages/Apply";
import { ExamList, ExamRun, ResultView } from "./pages/Exams";
import { VideoList, VideoDetail } from "./pages/Videos";
import { Corporate, Instructors, Faq, Posts, PostDetail, Contact, LegalList, LegalPage } from "./pages/ContentPages";
import { Login, Register, Forgot, Reset, VerifyEmail } from "./pages/Auth";
import StudentPanel from "./pages/StudentPanel";
import AdminLayout from "./pages/admin/AdminLayout";
import ResourceManager from "./pages/admin/ResourceManager";
import { Dashboard, Applications, Students, Contacts, EmailLogs, Results, Users, Settings, Logs } from "./pages/admin/AdminPages";
import { LessonList, LessonDetail } from "./pages/LessonNotes";
import CertificateVerify from "./pages/CertificateVerify";
import { LessonsAdmin, LessonEditor } from "./pages/admin/LessonsAdmin";
import Leads from "./pages/admin/school/Leads";
import Registrations, { MissingDocs } from "./pages/admin/school/Registrations";
import RegistrationDetail from "./pages/admin/school/RegistrationDetail";
import Classes from "./pages/admin/school/Classes";
import ClassDetail from "./pages/admin/school/ClassDetail";
import Finance from "./pages/admin/school/Finance";
import FinanceStudent from "./pages/admin/school/FinancePanel";
import Reports from "./pages/admin/school/Reports";
import Reminders from "./pages/admin/school/Reminders";
import Training from "./pages/admin/school/Training";
import Schedule from "./pages/admin/school/Schedule";
import OfficialExams from "./pages/admin/school/Exams";
import DocArchive from "./pages/admin/school/DocArchive";
import InstructorsPro from "./pages/admin/school/Instructors";
import Rooms from "./pages/admin/school/Rooms";
import Messaging from "./pages/admin/school/Messaging";

function Protected({ staff = false, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/kursiyer-girisi" state={{ from: loc.pathname }} replace />;
  if (staff && !isStaff(user)) return <Section><Empty text="Bu alana erişim yetkiniz yok." testid="forbidden" /></Section>;
  return children;
}

export default function App() {
  return (
    <Providers>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="kurumsal" element={<Corporate />} />
            <Route path="egitimlerimiz" element={<Courses />} />
            <Route path="egitimlerimiz/:slug" element={<CourseDetail />} />
            <Route path="egitim-takvimi" element={<CalendarPage />} />
            <Route path="kurs-basvurusu" element={<Apply />} />
            <Route path="deneme-sinavlari" element={<ExamList />} />
            <Route path="deneme-sinavlari/:id" element={<ExamRun />} />
            <Route path="sinav-sonucu/:sid" element={<ResultView />} />
            <Route path="egitim-videolari" element={<VideoList />} />
            <Route path="egitim-videolari/:id" element={<VideoDetail />} />
            <Route path="egitmenlerimiz" element={<Instructors />} />
            <Route path="ders-notlari" element={<LessonList />} />
            <Route path="ders-notlari/:slug" element={<LessonDetail />} />
            <Route path="belge-dogrula" element={<CertificateVerify />} />
            <Route path="sss" element={<Faq />} />
            <Route path="duyurular" element={<Posts kind="announcement" />} />
            <Route path="duyurular/:slug" element={<PostDetail kind="announcement" />} />
            <Route path="rehber" element={<Posts kind="guide" />} />
            <Route path="rehber/:slug" element={<PostDetail kind="guide" />} />
            <Route path="iletisim" element={<Contact />} />
            <Route path="yasal" element={<LegalList />} />
            <Route path="yasal/:slug" element={<LegalPage />} />
            <Route path="kursiyer-girisi" element={<Login />} />
            <Route path="kayit-ol" element={<Register />} />
            <Route path="sifremi-unuttum" element={<Forgot />} />
            <Route path="sifre-sifirla" element={<Reset />} />
            <Route path="e-posta-dogrula" element={<VerifyEmail />} />
            <Route path="kursiyer-paneli" element={<Protected><StudentPanel /></Protected>} />
            <Route path="*" element={<Section><Empty text="Sayfa bulunamadı." testid="not-found" /></Section>} />
          </Route>
          <Route path="yonetim" element={<Protected staff><AdminLayout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="basvurular" element={<Applications />} />
            <Route path="kursiyerler" element={<Students />} />
            <Route path="iletisim" element={<Contacts />} />
            <Route path="eposta" element={<EmailLogs />} />
            <Route path="sonuclar" element={<Results />} />
            <Route path="kullanicilar" element={<Users />} />
            <Route path="icerik" element={<Settings group="content" />} />
            <Route path="seo" element={<Settings group="seo" />} />
            <Route path="sistem" element={<Settings group="system" />} />
            <Route path="kvkk" element={<Settings group="kvkk" />} />
            <Route path="kayitlar" element={<Logs />} />
            <Route path="dersler" element={<LessonsAdmin />} />
            <Route path="dersler/:id" element={<LessonEditor />} />
            <Route path="adaylar" element={<Leads />} />
            <Route path="kursiyer-kayit" element={<Registrations />} />
            <Route path="kursiyer-kayit/:id" element={<RegistrationDetail />} />
            <Route path="siniflar" element={<Classes />} />
            <Route path="siniflar/:id" element={<ClassDetail />} />
            <Route path="eksik-evrak" element={<MissingDocs />} />
            <Route path="finans" element={<Finance />} />
            <Route path="finans/kursiyer/:id" element={<FinanceStudent />} />
            <Route path="raporlar" element={<Reports />} />
            <Route path="hatirlatmalar" element={<Reminders />} />
            <Route path="egitim" element={<Training />} />
            <Route path="ders-programi" element={<Schedule />} />
            <Route path="sinavlar" element={<OfficialExams />} />
            <Route path="evrak" element={<DocArchive />} />
            <Route path="egitmenler" element={<InstructorsPro />} />
            <Route path="derslikler" element={<Rooms />} />
            <Route path="iletisim-merkezi" element={<Messaging />} />
            <Route path="r/:name" element={<ResourceManager />} />
          </Route>
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </Providers>
  );
}
